import { useOptimisticUpdate } from "../../common/hooks/useOptimisticUpdate.ts";
import { useParams } from "react-router";
import { put } from "../../common/api/apiShared.ts";
import { ContractModel, ContractUpdateModel } from "./Contract.types.ts";
import { StepModel } from "../../common/components/dotLegalStepper/DotLegalCoreStepper.types.ts";
import { useContractSteps } from "./steps/useContractSteps.tsx";
import { EntityTemplate } from "../../entityTemplates/EntityTemplate.types.ts";
import { useValidateContractStep } from "./ContractValidator.hooks.ts";
import { useDotLegalQuery, useDotLegalQueryClient, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";

export function getContractQueryKey(contractId: string | undefined) {
    return "contracts" + contractId;
}

/**
 * The contract as the update endpoints take it. Exported so the AI-assisted update sends exactly what the
 * edit page sends; without that the two drift the first time a property is added.
 */
export function convertToUpdateModel(viewModel: ContractModel): ContractUpdateModel {
    return {
        name: viewModel.name,
        description: viewModel.description,
        contractTypeId: viewModel.contractTypeId!,
        referenceNumber: viewModel.referenceNumber,
        businessAreas: viewModel.businessAreas,
        groupEntities: viewModel.groupEntities,
        visibleToAllUsers: viewModel.visibleToAllUsers,
        allGroupEntityAccess: viewModel.allGroupEntityAccess,
        contractManagerId: viewModel.contractManagerId,
        contractExpiryNotificationInDays: viewModel.contractExpiryNotificationInDays,
        endDate: viewModel.endDate,
        signatureDate: viewModel.signatureDate,
        startDate: viewModel.startDate,
        currencyCode: viewModel.currencyCode,
        finalizedAmount: viewModel.finalizedAmount,
        paymentTermId: viewModel.paymentTermId,
        specialTerms: viewModel.specialTerms,
        totalAmount: viewModel.totalAmount,
        note: viewModel.note,
        additionalAccessUsers: viewModel.additionalAccessUsers,
        excludedUsers: viewModel.excludedUsers,
        contractAccessBusinessAreas: viewModel.contractAccessBusinessAreas,
        autoRenewalDuration: viewModel.autoRenewalDuration,
        autoRenewalDurationType: viewModel.autoRenewalDurationType,
        status: viewModel.status,
    };
}

export function useContract() {
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const queryClient = useDotLegalQueryClient();

    const { id } = useParams<{ id: string }>();

    const queryKey = getContractQueryKey(id);
    let { data: contractModel, isLoading, refetch: refetchContract } = useDotLegalQueryWithKey<ContractModel>(queryKey, `/contracts/${id}`);

    let entityTemplateUrl = `/entity-templates/${contractModel?.entityTemplateId}`;
    let entityTemplateQuery = useDotLegalQuery<EntityTemplate>(entityTemplateUrl, {
        enabled: !!contractModel,
    });

    const { validateStep } = useValidateContractStep(id ?? "");

    function getSteps() {
        let steps: Array<StepModel> = [];

        if (entityTemplateQuery.data) {
            entityTemplateQuery.data.steps
                .sort((a, b) => a.order - b.order)
                .forEach((step) => {
                    steps.push(new StepModel(step.name, step.order, () => validateStep(step, contractModel!)));
                });
        }

        return steps;
    }

    const steps = useContractSteps(contractModel ?? new ContractModel(), entityTemplateQuery.data, getSteps());

    function changeStep(step: number) {
        steps.changeStep(step);
    }

    async function onChange(changeFunc: (contract: ContractModel) => void) {
        let tempContact = { ...contractModel! };

        changeFunc(tempContact);
        await save(tempContact);
    }

    function onCustomFieldChange(changeFunc: (contract: ContractModel) => void) {
        let tempContact = { ...contractModel! };

        changeFunc(tempContact);
        queryClient.setQueryData(queryKey, tempContact);
    }

    async function save(contract: ContractModel) {
        await putOnQueueAndSetQueryData(contract, queryKey, updateContractAPI, contract);
    }

    return {
        steps: steps.getSteps(),
        isLoading: isLoading || entityTemplateQuery.isLoading,
        contractTemplate: entityTemplateQuery.data,
        activeStep: steps.getActiveStep(),
        changeStep,
        contract: contractModel!,
        validationErrors: steps.validationErrors,
        onChange,
        onCustomFieldChange,
        contractId: id,
        refetchContract,
    };

    async function updateContractAPI(viewModel: ContractModel) {
        return await put<ContractModel>("/contracts/" + id, convertToUpdateModel(viewModel));
    }
}
